import { useContext, useState } from 'react';
import { shareContext } from '../ShareContext';
import { useQuery } from '@apollo/client';
import {
  GET_VISIBLE_DOCUMENTS_FROM_FOLDER_QUERY,
  GetVisibleDocumentsFromFolderOutput,
} from 'graphql/document/DocumentQueries';
import { useSelectedLinkContext } from 'contexts';
import { getSortedDocuments } from '../Helpers';
import { SharedDocument } from 'types/Document';
import DocumentItem from './FolderContentExplorer/DocumentItem';
import useStyles from './FolderContentExplorer.styles';
import { DropzoneState } from 'react-dropzone';
import Description from './FolderContentExplorer/DeleteMode/Description';
import { deleteModeContext } from './FolderContentExplorer/DeleteMode/DeleteModeContext';
import { ImageViewerContextProvider } from 'components/medias/ImageViewer/ImageViewerContext';
import ImageViewer from 'components/medias/ImageViewer/ImageViewer';
import { filterImageDocument } from 'utils/document';

type FolderContentExplorerPureProps = {
  documents: SharedDocument[];
  dropzoneState: DropzoneState;
};

const FolderContentExplorerPure = ({
  documents,
  dropzoneState,
}: FolderContentExplorerPureProps): JSX.Element => {
  const classes = useStyles();
  const { getRootProps, getInputProps } = dropzoneState;

  return (
    <>
      <div
        className={classes.dragZone}
        data-testid="dropzone"
        {...getRootProps({
          onClick: (event) => event.stopPropagation(),
        })}
      >
        <Description />

        <input {...getInputProps()} />

        {documents.map((document) => (
          <DocumentItem document={document} key={document.id} />
        ))}
      </div>

      <ImageViewer />
    </>
  );
};

type FilterDocumentsInputType = {
  currentUserKey: string | undefined;
  documents: SharedDocument[];
  isDeleteMode: boolean;
};

const filterDocuments = ({
  currentUserKey,
  documents,
  isDeleteMode,
}: FilterDocumentsInputType) => {
  if (isDeleteMode && currentUserKey) {
    return documents.filter((document) => document.creator === currentUserKey);
  }

  return documents;
};

const FolderContentExplorer = (): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const { dropzoneState, selectedFolder } = useContext(shareContext);
  const { isDeleteMode } = useContext(deleteModeContext);
  const [currentViewedImage, setCurrentViewedImage] =
    useState<SharedDocument>();

  const { data } = useQuery<GetVisibleDocumentsFromFolderOutput>(
    GET_VISIBLE_DOCUMENTS_FROM_FOLDER_QUERY,
    {
      variables: {
        getVisibleDocumentsFromFolderInput: {
          channel: selectedLink?.channelKey,
          folderKey: selectedFolder?.key,
        },
      },
    },
  );

  if (!dropzoneState) {
    return null;
  }

  const sortedDocuments = getSortedDocuments(
    data?.visibleDocumentsFromFolder.documents || [],
  );

  const imageDocuments = filterImageDocument(sortedDocuments);

  const filteredAndSortedDocuments = filterDocuments({
    currentUserKey: selectedLink?.userKey,
    documents: sortedDocuments,
    isDeleteMode,
  });

  return (
    <ImageViewerContextProvider
      currentViewedImage={currentViewedImage}
      imageDocuments={imageDocuments}
      setCurrentViewedImage={setCurrentViewedImage}
    >
      <FolderContentExplorerPure
        documents={filteredAndSortedDocuments}
        dropzoneState={dropzoneState}
      />
    </ImageViewerContextProvider>
  );
};

export default FolderContentExplorer;
